/**
 * AI 문서 아카이브 — 허브
 * 카드 4개(OCR·계약정보·인사정보·전자서명) 클릭 시 해당 기능 화면으로 이동한다.
 * 지표는 목업이 아니라 로컬 스토어(archiveDocStore/signatureStore)의 실제 저장 건수를 센다.
 * 설계: docs/agent-plans/CM/20260923-AI문서아카이브-OCR전자서명계약인사문서-통합설계분석.md
 */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTab } from 'contexts/TabContext';
import useTenantCode from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, cssVar } from 'styles/theme';
import { ARCHIVE_CARD_TABS } from 'config/archiveMenuConfig';
import { loadDocInstances, docTypeOf } from 'archive/archiveDocStore';
import { loadRequests } from 'archive/signatureStore';

const Intro = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  margin: 0 2px ${theme.spacing.sm};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${theme.spacing.md};
`;

const CardBox = styled.button`
  text-align: left;
  cursor: pointer;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: var(--erp-radius-md, ${theme.borderRadius.md});
  background: ${cssVar('color-bg-white', theme.colors.white)};
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
    border-color: var(--erp-color-primary, ${cssVar('color-primary', theme.colors.primary)});
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardIcon = styled.span`
  font-size: 22px;
`;

const CardTitle = styled.div`
  font-size: ${theme.typography.fontSize.md};
  font-weight: 700;
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const CardDesc = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  line-height: 1.5;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  border-top: 1px solid ${cssVar('color-border', theme.colors.border)};
  padding-top: 8px;
  margin-top: 4px;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => (p.$warn ? 'rgba(200, 80, 40, 0.12)' : 'rgba(60, 120, 220, 0.12)')};
  color: ${(p) => (p.$warn ? '#c0392b' : 'var(--erp-color-primary, #3c78dc)')};
`;

const CARD_DEFS = [
  {
    docCategory: 'OCR',
    icon: '🧾',
    title: '지출·세금 증빙 OCR',
    desc: '카드영수증·세금계산서를 촬영/업로드해 가맹점·품목·금액을 인식하고 아카이브에 적재합니다.',
  },
  {
    docCategory: 'CONTRACT',
    icon: '📑',
    title: '계약정보 아카이브',
    desc: '계약서·통장사본·사업자등록증에서 상대방·금액·계좌 정보를 추출해 관리합니다.',
  },
  {
    docCategory: 'HR',
    icon: '🗂',
    title: '인사정보 아카이브',
    desc: '등본·가족관계증명서·근로계약서 등 인사서류를 데이터화합니다. 민감정보 포함(주민번호 마스킹).',
    sensitive: true,
  },
  {
    docCategory: 'SIGNATURE',
    icon: '✍️',
    title: '전자서명',
    desc: '양식·서명영역을 지정해 저장하고, 링크로 외부 서명을 요청·관리합니다.',
  },
];

function monthCount(docs, monthPrefix) {
  return docs.filter((d) => String(d.createdAt || '').slice(0, 7) === monthPrefix).length;
}

const ArchiveHub = () => {
  const { openTab } = useTab();
  const tenantCode = useTenantCode();

  const stats = useMemo(() => {
    const docs = loadDocInstances(tenantCode);
    const requests = loadRequests(tenantCode);
    const ym = new Date().toISOString().slice(0, 7);
    const byCategory = {};
    CARD_DEFS.forEach((c) => {
      byCategory[c.docCategory] =
        c.docCategory === 'SIGNATURE'
          ? { total: requests.length, month: monthCount(requests, ym) }
          : {
              total: docs.filter((d) => docTypeOf(d.docType)?.category === c.docCategory).length,
              month: monthCount(
                docs.filter((d) => docTypeOf(d.docType)?.category === c.docCategory),
                ym
              ),
              pending: docs.filter(
                (d) => docTypeOf(d.docType)?.category === c.docCategory && d.status === 'PENDING'
              ).length,
            };
    });
    return byCategory;
  }, [tenantCode]);

  const openCard = (docCategory) => {
    const tab = ARCHIVE_CARD_TABS.find((t) => t.docCategory === docCategory);
    if (!tab) return;
    const { docCategory: _omit, ...tabData } = tab;
    openTab(tabData);
  };

  return (
    <PageContainer>
      <Intro>
        문서를 촬영·업로드하면 표준 필드로 정리해 아카이브에 쌓입니다. 지출결의·계약대장·인사마스터 등
        업무 화면에도 같은 기능이 내장되며, 여기서는 종류별로 모아 조회·관리합니다.
      </Intro>
      <Grid>
        {CARD_DEFS.map((c) => {
          const s = stats[c.docCategory] || { total: 0, month: 0, pending: 0 };
          return (
            <CardBox key={c.docCategory} type="button" onClick={() => openCard(c.docCategory)}>
              <CardTop>
                <CardIcon>{c.icon}</CardIcon>
                {c.sensitive && <Badge $warn>민감정보</Badge>}
              </CardTop>
              <CardTitle>{c.title}</CardTitle>
              <CardDesc>{c.desc}</CardDesc>
              <CardMeta>
                <span>누적 {s.total}건 · 이번 달 {s.month}건</span>
                {typeof s.pending === 'number' && s.pending > 0 && <Badge>검토대기 {s.pending}</Badge>}
              </CardMeta>
            </CardBox>
          );
        })}
      </Grid>
    </PageContainer>
  );
};

export default ArchiveHub;
